import { formatDBDate } from "@utils/general/format-db-date";

interface Invoice {
  invoice_id: number;
  invoice_code: string;
  invoice_date: string;
  total_due: number;
  "0-30": number;
  "31-60": number;
  "61-90": number;
  "90+": number;
}

interface Customer {
  customer_id: number;
  customer_name: string;
  customer_phone: string;
  business_unit_name: string;
  invoices: Invoice[];
  total_due: number;
  "total_0-30": number;
  "total_31-60": number;
  "total_61-90": number;
  "total_90+": number;
  total: number;
}

function formatCurrency(value: number) {
  return value.toLocaleString("en-US");
}

function ReceivablesTable({ data }: { data?: { customers?: Customer[] } }) {
  const customers: Customer[] = data?.customers || [];

  const grandTotal = customers.reduce(
    (acc, c) => ({
      total_due: acc.total_due + Number(c.total_due || 0),
      "0-30": acc["0-30"] + Number(c["total_0-30"] || 0),
      "31-60": acc["31-60"] + Number(c["total_31-60"] || 0),
      "61-90": acc["61-90"] + Number(c["total_61-90"] || 0),
      "90+": acc["90+"] + Number(c["total_90+"] || 0),
    }),
    { total_due: 0, "0-30": 0, "31-60": 0, "61-90": 0, "90+": 0 }
  );

  if (!customers.length) {
    return (
      <div className="flex items-center justify-center py-16 text-gray-400 text-sm">
        No data available.
      </div>
    );
  }

  return (
    <div className="overflow-x-auto border border-gray-200">
      <table className="w-full border-collapse text-sm">
        <thead>
          <tr className="bg-[#002C76] text-white">
            <th className="px-4 py-3 text-left font-semibold whitespace-nowrap">Invoice ID</th>
            <th className="px-4 py-3 text-left font-semibold whitespace-nowrap">Date</th>
            <th className="px-4 py-3 text-right font-semibold whitespace-nowrap">Total Due</th>
            <th className="px-4 py-3 text-right font-semibold whitespace-nowrap">0-30</th>
            <th className="px-4 py-3 text-right font-semibold whitespace-nowrap">31-60</th>
            <th className="px-4 py-3 text-right font-semibold whitespace-nowrap">61-90</th>
            <th className="px-4 py-3 text-right font-semibold whitespace-nowrap">90+</th>
          </tr>
        </thead>
        <tbody>
          {customers.map((customer) => (
            <>
              {/* Customer Header Row */}
              <tr key={`customer-header-${customer.customer_id}`}>
                <td colSpan={7} className="px-4 py-2 bg-blue-100">
                  <span className="font-semibold text-[#002C76]">
                    {customer.customer_name}
                  </span>
                  {customer.customer_phone && (
                    <span className="ml-3 text-gray-500 text-xs">
                      {customer.customer_phone}
                    </span>
                  )}
                </td>
              </tr>

              {/* Invoice Rows */}
              {customer.invoices.map((invoice, idx) => (
                <tr
                  key={`invoice-${invoice.invoice_id}`}
                  className={idx % 2 === 0 ? "bg-white" : "bg-gray-50"}
                >

                  <td className="px-4 py-2 text-gray-700 font-mono text-xs">{invoice.invoice_code}</td>
                  <td className="px-4 py-2 text-gray-600 whitespace-nowrap">
                    {invoice.invoice_date ? formatDBDate(invoice.invoice_date) : "-"}
                  </td>
                  <td className="px-4 py-2 text-right text-gray-800 font-medium">
                    {formatCurrency(Number(invoice.total_due || 0))}
                  </td>
                  <td className="px-4 py-2 text-right text-gray-600">
                    {formatCurrency(Number(invoice["0-30"] || 0))}
                  </td>
                  <td className="px-4 py-2 text-right text-gray-600">
                    {formatCurrency(Number(invoice["31-60"] || 0))}
                  </td>
                  <td className="px-4 py-2 text-right text-gray-600">
                    {formatCurrency(Number(invoice["61-90"] || 0))}
                  </td>
                  <td className="px-4 py-2 text-right text-red-600 font-medium">
                    {formatCurrency(Number(invoice["90+"] || 0))}
                  </td>
                </tr>
              ))}

              {/* Customer Total Row */}
              <tr
                key={`customer-total-${customer.customer_id}`}
                className="bg-blue-50 border-t-2 border-blue-200"
              >
                <td colSpan={2} className="px-4 py-2 font-bold text-[#002C76] text-right">
                  Total:
                </td>
                <td className="px-4 py-2 text-right font-bold text-[#002C76]">
                  {formatCurrency(Number(customer.total_due || 0))}
                </td>
                <td className="px-4 py-2 text-right font-bold text-[#002C76]">
                  {formatCurrency(Number(customer["total_0-30"] || 0))}
                </td>
                <td className="px-4 py-2 text-right font-bold text-[#002C76]">
                  {formatCurrency(Number(customer["total_31-60"] || 0))}
                </td>
                <td className="px-4 py-2 text-right font-bold text-[#002C76]">
                  {formatCurrency(Number(customer["total_61-90"] || 0))}
                </td>
                <td className="px-4 py-2 text-right font-bold text-red-700">
                  {formatCurrency(Number(customer["total_90+"] || 0))}
                </td>
              </tr>

              {/* Spacer */}
              <tr key={`spacer-${customer.customer_id}`}>
                <td colSpan={7} className="py-1" />
              </tr>
            </>
          ))}

          {/* Grand Total */}
          <tr className="bg-[#002C76] text-white border-t-2 border-[#002C76]">
            <td colSpan={2} className="px-4 py-3 font-bold text-right">
              Grand Total:
            </td>
            <td className="px-4 py-3 text-right font-bold">
              {formatCurrency(grandTotal.total_due)}
            </td>
            <td className="px-4 py-3 text-right font-bold">
              {formatCurrency(grandTotal["0-30"])}
            </td>
            <td className="px-4 py-3 text-right font-bold">
              {formatCurrency(grandTotal["31-60"])}
            </td>
            <td className="px-4 py-3 text-right font-bold">
              {formatCurrency(grandTotal["61-90"])}
            </td>
            <td className="px-4 py-3 text-right font-bold">
              {formatCurrency(grandTotal["90+"])}
            </td>
          </tr>

          {/* Receivables Account Row */}
          <tr className="bg-gray-100 border-t border-gray-300">
            <td colSpan={2} className="px-4 py-3 font-bold text-right text-gray-700">
              Receivables Account:
            </td>
            <td className="px-4 py-3 text-right font-bold text-[#002C76] text-base">
              {formatCurrency(grandTotal.total_due)}
            </td>
            <td colSpan={4} />
          </tr>
        </tbody>
      </table>
    </div>
  );
}

export default ReceivablesTable;
